import { useEffect, useRef, useState, type DragEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, ClipboardPaste, Link2 } from 'lucide-react';
import { Disclaimer } from '@/components/common/Disclaimer';
import { PlatformIcon } from '@/components/icons/PlatformIcon';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { MediaPreview } from './MediaPreview';
import {
  AUDIO_BITRATE_KBPS,
  MAX_SNAPSHOT_INTERVAL,
  MIN_SNAPSHOT_INTERVAL,
  QUALITY_OPTIONS,
  SNAPSHOT_FORMATS,
  SNAPSHOT_INTERVALS,
} from '@/constants/limits';
import { PLATFORMS } from '@/constants/platforms';
import { useI18n } from '@/hooks/useI18n';
import { usePlatformPreview } from '@/hooks/usePlatformPreview';
import { useUrlProcessor } from '@/hooks/useUrlProcessor';
import { useSettingsStore } from '@/store/settingsStore';
import { cn } from '@/utils/cn';
import { extractUrlFromText, urlFormSchema, type UrlFormValues } from '@/utils/url';
import type { OutputType, QualityPreset, SnapshotFormat } from '@/types';

/** Focus the URL box from anywhere (keyboard shortcuts). */
export const FOCUS_URL_EVENT = 'dmmt:focus-url';
/** Paste from clipboard into the URL box (keyboard shortcuts). */
export const PASTE_URL_EVENT = 'dmmt:paste-url';

const OUTPUT_TYPES: OutputType[] = ['video', 'audio', 'snapshots'];

export function UrlInput() {
  const { t } = useI18n();
  const defaultOutput = useSettingsStore((s) => s.defaultOutput);
  const defaultQuality = useSettingsStore((s) => s.defaultQuality);
  const defaultInterval = useSettingsStore((s) => s.snapshotInterval);
  const defaultFormat = useSettingsStore((s) => s.snapshotFormat);

  const [outputType, setOutputType] = useState<OutputType>(defaultOutput);
  const [quality, setQuality] = useState<QualityPreset>(defaultQuality);
  const [intervalSec, setIntervalSec] = useState<number>(defaultInterval);
  const [customInterval, setCustomInterval] = useState(!SNAPSHOT_INTERVALS.includes(defaultInterval as 1 | 3 | 5));
  const [snapshotFormat, setSnapshotFormat] = useState<SnapshotFormat>(defaultFormat);
  const [dragOver, setDragOver] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const processor = useUrlProcessor();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<UrlFormValues>({
    resolver: zodResolver(urlFormSchema),
    defaultValues: { url: '' },
    mode: 'onChange',
  });

  const url = watch('url');
  const preview = usePlatformPreview(url ?? '');
  const { ref: registerUrlRef, ...urlField } = register('url');

  const handlePasteClick = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const pastedUrl = extractUrlFromText(text);
      if (pastedUrl) setValue('url', pastedUrl, { shouldValidate: true });
    } catch {
      inputRef.current?.focus();
    }
  };

  const pasteRef = useRef(handlePasteClick);
  pasteRef.current = handlePasteClick;

  useEffect(() => {
    const focus = () => inputRef.current?.focus();
    const paste = () => void pasteRef.current();
    window.addEventListener(FOCUS_URL_EVENT, focus);
    window.addEventListener(PASTE_URL_EVENT, paste);
    return () => {
      window.removeEventListener(FOCUS_URL_EVENT, focus);
      window.removeEventListener(PASTE_URL_EVENT, paste);
    };
  }, []);

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    const text =
      event.dataTransfer.getData('text/uri-list') || event.dataTransfer.getData('text/plain');
    const droppedUrl = extractUrlFromText(text);
    if (droppedUrl) setValue('url', droppedUrl, { shouldValidate: true });
  };

  const onSubmit = handleSubmit((values) => {
    processor.mutate(
      {
        url: values.url,
        outputType,
        quality: outputType === 'video' ? quality : undefined,
        snapshot:
          outputType === 'snapshots' ? { intervalSec, format: snapshotFormat } : undefined,
      },
      { onSuccess: () => reset() }
    );
  });

  const outputLabel: Record<OutputType, string> = {
    video: t('input.output.video'),
    audio: t('input.output.audio'),
    snapshots: t('input.output.snapshots'),
  };

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={cn(
        'glass-strong space-y-5 p-5 transition-all sm:p-6',
        dragOver && 'border-primary-500/60 shadow-glow'
      )}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Link2 className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" aria-hidden />
            <input
              {...urlField}
              ref={(element) => {
                registerUrlRef(element);
                inputRef.current = element;
              }}
              type="url"
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
              placeholder={dragOver ? t('input.dropHint') : t('input.placeholder')}
              aria-label="Media URL"
              className="h-12 w-full rounded-2xl border border-white/10 bg-white/5 pl-11 pr-12 text-sm text-zinc-100 transition-colors placeholder:text-zinc-500 focus-ring focus:border-primary-500/50"
            />
            {preview.platform && (
              <span
                className="absolute right-4 top-1/2 -translate-y-1/2"
                style={{ color: PLATFORMS[preview.platform].color }}
                title={PLATFORMS[preview.platform].name}
              >
                <PlatformIcon platform={preview.platform} className="h-5 w-5" />
              </span>
            )}
          </div>

          <div className="flex gap-3">
            <Button type="button" variant="secondary" onClick={handlePasteClick} className="flex-1 sm:flex-none">
              <ClipboardPaste className="h-4 w-4" aria-hidden />
              {t('input.paste')}
            </Button>
            <Button type="submit" loading={processor.isPending} className="flex-1 sm:flex-none">
              {processor.isPending ? t('input.processing') : t('input.process')}
            </Button>
          </div>
        </div>

        {errors.url && url && (
          <p className="flex items-center gap-1.5 text-xs text-red-400">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
            {errors.url.message}
          </p>
        )}
        {preview.publiclyAccessible === false && (
          <p className="flex items-center gap-1.5 text-xs text-amber-400">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
            This content looks private or unavailable — only public posts can be processed.
          </p>
        )}

        {/* Output type */}
        <div className="flex rounded-2xl border border-white/10 bg-white/5 p-1" role="tablist" aria-label="Output type">
          {OUTPUT_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              role="tab"
              aria-selected={outputType === type}
              onClick={() => setOutputType(type)}
              className={cn(
                'flex-1 rounded-xl px-3 py-2 text-xs font-semibold transition-all sm:text-sm',
                outputType === type
                  ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white shadow-glow-sm'
                  : 'text-zinc-400 hover:text-zinc-200'
              )}
            >
              {outputLabel[type]}
            </button>
          ))}
        </div>

        {/* Per-output options */}
        {outputType === 'video' && (
          <div className="max-w-xs">
            <Select
              label={t('input.quality')}
              value={quality}
              onChange={(event) => setQuality(event.target.value as QualityPreset)}
              options={QUALITY_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
            />
          </div>
        )}

        {outputType === 'audio' && (
          <p className="text-xs text-zinc-500">MP3 · {AUDIO_BITRATE_KBPS} kbps · extracted from the source video</p>
        )}

        {outputType === 'snapshots' && (
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <span className="mb-1.5 block text-sm font-medium text-zinc-300">{t('input.interval')}</span>
              <div className="flex rounded-2xl border border-white/10 bg-white/5 p-1">
                {SNAPSHOT_INTERVALS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => {
                      setCustomInterval(false);
                      setIntervalSec(preset);
                    }}
                    className={cn(
                      'rounded-xl px-3.5 py-1.5 text-xs font-semibold transition-all',
                      !customInterval && intervalSec === preset
                        ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white'
                        : 'text-zinc-400 hover:text-zinc-200'
                    )}
                  >
                    {preset}s
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setCustomInterval(true)}
                  className={cn(
                    'rounded-xl px-3.5 py-1.5 text-xs font-semibold transition-all',
                    customInterval
                      ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white'
                      : 'text-zinc-400 hover:text-zinc-200'
                  )}
                >
                  {t('input.custom')}
                </button>
              </div>
            </div>

            {customInterval && (
              <div className="w-28">
                <label className="mb-1.5 block text-sm font-medium text-zinc-300" htmlFor="custom-interval">
                  {t('input.seconds')}
                </label>
                <input
                  id="custom-interval"
                  type="number"
                  min={MIN_SNAPSHOT_INTERVAL}
                  max={MAX_SNAPSHOT_INTERVAL}
                  value={intervalSec}
                  onChange={(event) => {
                    const value = Number(event.target.value);
                    if (!Number.isNaN(value)) {
                      setIntervalSec(Math.min(MAX_SNAPSHOT_INTERVAL, Math.max(MIN_SNAPSHOT_INTERVAL, value)));
                    }
                  }}
                  className="h-10 w-full rounded-2xl border border-white/10 bg-white/5 px-3 text-sm text-zinc-100 focus-ring focus:border-primary-500/50"
                />
              </div>
            )}

            <div className="w-28">
              <Select
                label={t('input.format')}
                value={snapshotFormat}
                onChange={(event) => setSnapshotFormat(event.target.value as SnapshotFormat)}
                options={SNAPSHOT_FORMATS.map((format) => ({ value: format.value, label: format.label }))}
              />
            </div>
          </div>
        )}
      </form>

      {preview.isLoading && <Skeleton className="h-24" />}
      {preview.metadata && (
        <MediaPreview
          metadata={preview.metadata}
          outputType={outputType}
          quality={quality}
          intervalSec={intervalSec}
        />
      )}

      <Disclaimer />
    </div>
  );
}
